import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionsApi } from '../api/endpoints/subscriptions';
import toast from 'react-hot-toast';
import type {
  UpdateInstituteSubscriptionSettingsRequest,
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
} from '../types/institute';

export function useSubscriptions() {
  const queryClient = useQueryClient();

  const useInstituteSubscriptionSettings = (instituteId: number | null) =>
    useQuery({
      queryKey: ['admin-institute-subscription', instituteId],
      queryFn: () =>
        subscriptionsApi.getInstituteSubscriptionSettings(instituteId!).then((r) => r.data.data),
      enabled: !!instituteId,
    });

  const useUpdateInstituteSubscriptionSettings = (instituteId: number) =>
    useMutation({
      mutationFn: (request: UpdateInstituteSubscriptionSettingsRequest) =>
        subscriptionsApi.updateInstituteSubscriptionSettings(instituteId, request),
      onSuccess: (_, vars) => {
        const msg =
          vars.mode === 'InstitutePays'
            ? 'Institute pays mode enabled — all students marked as subscribed'
            : 'Subscription settings updated';
        toast.success(msg);
        queryClient.invalidateQueries({ queryKey: ['admin-institute-subscription', instituteId] });
      },
      onError: () => toast.error('Failed to update subscription settings'),
    });

  const usePlans = () =>
    useQuery({
      queryKey: ['admin-subscription-plans'],
      queryFn: () => subscriptionsApi.getAllPlans().then((r) => r.data.data),
    });

  const useCreatePlan = () =>
    useMutation({
      mutationFn: (request: CreateSubscriptionPlanRequest) =>
        subscriptionsApi.createPlan(request),
      onSuccess: () => {
        toast.success('Plan created');
        queryClient.invalidateQueries({ queryKey: ['admin-subscription-plans'] });
      },
      onError: () => toast.error('Failed to create plan'),
    });

  const useUpdatePlan = () =>
    useMutation({
      mutationFn: ({ planId, request }: { planId: number; request: UpdateSubscriptionPlanRequest }) =>
        subscriptionsApi.updatePlan(planId, request),
      onSuccess: () => {
        toast.success('Plan updated');
        queryClient.invalidateQueries({ queryKey: ['admin-subscription-plans'] });
      },
      onError: () => toast.error('Failed to update plan'),
    });

  const useDeletePlan = () =>
    useMutation({
      mutationFn: (planId: number) => subscriptionsApi.deletePlan(planId),
      onSuccess: () => {
        toast.success('Plan removed');
        queryClient.invalidateQueries({ queryKey: ['admin-subscription-plans'] });
      },
      onError: () => toast.error('Failed to delete plan'),
    });

  return {
    useInstituteSubscriptionSettings,
    useUpdateInstituteSubscriptionSettings,
    usePlans,
    useCreatePlan,
    useUpdatePlan,
    useDeletePlan,
  };
}
